import { ImeasurementPoint, ImeasurementPointAnswer, ImeasurementPointSelectOption } from '../models';
import { measurementPointTypesEnum } from '../models-enums';

/*
 * "In Use / Not In Use" gate measurement points are ordinary data-driven Select points - there is no
 * structural flag marking them. They are identified by their raw DB label (English source text),
 * NEVER by the translated display text (translations are display-only, see measurementPointTextUtil).
 * The label variants below cover every live gate found in staging/production data; they mirror the
 * API report skip logic in CustomTelerikRenderer.GetSkips, which keys off the same DB labels.
 */

// Canonical form: lowercased, whitespace collapsed, no spaces around slashes
const GATE_LABELS = new Set(['in use', 'in use/not in use', 'is in use?', 'in use?', 'not in use']);

// Option labels that mean the equipment IS in use (gates use In Use/Not in Use or Yes/No pairs)
const ACTIVE_OPTION_LABELS = new Set(['in use', 'yes']);

// Option labels that mean the equipment is explicitly NOT in use. Deliberately separate from the
// absence of an answer: an unanswered gate must keep its tab visible so the engineer can answer it.
const INACTIVE_OPTION_LABELS = new Set(['not in use', 'no']);

const normalizeText = (text: string): string =>
    text
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .replace(/\s*\/\s*/g, '/')
        .trim();

const hasLiveOptions = (measurementPoint: ImeasurementPoint): boolean =>
    (measurementPoint.selectOptions ?? []).some(
        (option: ImeasurementPointSelectOption) => option.isDeleted !== true
    );

/*
 * Find the tab's In Use gate, if any. A gate must be a Select with a recognized label and at least
 * one live option (some production gates have zero options - treating those as gates would hide the
 * tab's fields forever, so they are ignored).
 */
export const findInUseGate = (measurementPoints: ImeasurementPoint[]): ImeasurementPoint | undefined =>
    measurementPoints.find(
        measurementPoint =>
            measurementPoint.type === measurementPointTypesEnum.select &&
            measurementPoint.isDeleted !== true &&
            GATE_LABELS.has(normalizeText(measurementPoint.label ?? '')) &&
            hasLiveOptions(measurementPoint)
    );

/*
 * Extract the selected option ID from a select control value, which can be an Ioption object,
 * a raw option ID string, or empty.
 */
export const getSelectedOptionID = (value: any): string | null => {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    if (typeof value === 'object') {
        return value.value ?? null;
    }
    return value;
};

/*
 * Resolve a selected option ID to its live option, or undefined. Shared by the label predicates so
 * they all skip soft-deleted options and unknown IDs identically.
 */
const findLiveOption = (
    gate: ImeasurementPoint,
    selectedOptionID: string | null | undefined
): ImeasurementPointSelectOption | undefined => {
    if (!selectedOptionID) {
        return undefined;
    }
    return (gate.selectOptions ?? []).find(
        (candidate: ImeasurementPointSelectOption) =>
            candidate.id === selectedOptionID && candidate.isDeleted !== true
    );
};

/*
 * The gate is "active" only when the selected option's raw DB label says the equipment is in use.
 * Unanswered gates and unrecognized options count as inactive (fields stay hidden until the user
 * explicitly selects In Use / Yes - the behavior approved on MMG-4268).
 */
export const isInUseGateActive = (
    gate: ImeasurementPoint,
    selectedOptionID: string | null | undefined
): boolean => {
    const option = findLiveOption(gate, selectedOptionID);
    return option !== undefined && ACTIVE_OPTION_LABELS.has(normalizeText(option.label ?? ''));
};

/*
 * The stricter counterpart to isInUseGateActive: true ONLY when the engineer has explicitly said the
 * equipment is not in use. Unanswered, cleared, deleted and unrecognised options all return false,
 * so a tab is never hidden on data we do not understand (MMG-4405).
 */
export const isInUseGateExplicitlyNotInUse = (
    gate: ImeasurementPoint,
    selectedOptionID: string | null | undefined
): boolean => {
    const option = findLiveOption(gate, selectedOptionID);
    return option !== undefined && INACTIVE_OPTION_LABELS.has(normalizeText(option.label ?? ''));
};

/*
 * True once the gate carries a recognised answer either way. Drives hiding the gate dropdown itself:
 * an answered gate is clutter, an unanswered one is the only control the engineer can act on.
 *
 * Must be fed resolveGateAnsweredOptionID, NEVER resolveGateSelectedOptionID: the latter falls back to
 * the gate's configured selectDefaultOptionID, so a gate defaulted to Not In Use would report answered
 * and hide its own dropdown (and later its whole tab) before any engineer had touched the asset.
 */
export const isInUseGateAnswered = (
    gate: ImeasurementPoint,
    selectedOptionID: string | null | undefined
): boolean =>
    isInUseGateActive(gate, selectedOptionID) || isInUseGateExplicitlyNotInUse(gate, selectedOptionID);

/*
 * The option the gate has actually been set to: undefined when nobody has set it, null when the
 * engineer cleared it this session. A configured selectDefaultOptionID is deliberately NOT an answer
 * here - hiding a tab or its dropdown on a default nobody chose would strand the engineer on a tab
 * they never saw (MMG-4405).
 */
export const resolveGateAnsweredOptionID = (
    gate: ImeasurementPoint,
    answers: ImeasurementPointAnswer[] | undefined,
    gateSelections: { [gateID: string]: string | null }
): string | null | undefined => {
    const liveOptionID = gateSelections[gate.id];
    if (liveOptionID !== undefined) {
        return liveOptionID;
    }
    const gateAnswer = (answers ?? []).find(answer => answer.measurementPointID === gate.id);
    return gateAnswer !== undefined ? gateAnswer.measurementPointSelectOptionID ?? null : undefined;
};

/*
 * Apply the gate's configured selectDefaultOptionID when nobody has answered it. Shared so the
 * selected ID can be derived from the answered ID instead of re-resolving it (MMG-4405).
 */
const applyDefaultFallback = (gate: ImeasurementPoint, answeredOptionID: string | null | undefined): string | null =>
    answeredOptionID !== undefined ? answeredOptionID : gate.selectDefaultOptionID ?? null;

/*
 * Resolve the option ID the gate is currently set to. A live selection (the user touching the
 * dropdown this session) always wins, INCLUDING when it is null because they cleared the gate.
 *
 * The live selection must win because the answers passed in are not necessarily the user's: on a
 * fresh (temporary) result the form seeds select answers from the previous inspection when the
 * measurement point has selectRememberBetweenInspection set - true for ~93% of live gates. Reading
 * the answer there would snap the gate back to last inspection's value on every re-render.
 *
 * This single resolved ID drives both the gate control's own value and the hide/show decision for
 * the rest of the tab, so the two can never disagree.
 */
export const resolveGateSelectedOptionID = (
    gate: ImeasurementPoint,
    answers: ImeasurementPointAnswer[] | undefined,
    gateSelections: { [gateID: string]: string | null }
): string | null => applyDefaultFallback(gate, resolveGateAnsweredOptionID(gate, answers, gateSelections));

/*
 * Everything the form needs to know about one tab's In Use gate, resolved in a single pass.
 *
 * The two rules read DIFFERENT option IDs and must never be crossed, which is why this lives here
 * (testable) rather than in the component (not - there is no React Testing Library in this repo):
 *
 *   - MMG-4268 hides the rest of the tab off the SELECTED option, which includes the gate's
 *     configured selectDefaultOptionID. That same value seeds what the dropdown displays.
 *   - MMG-4405 hides the answered dropdown, and the whole tab, off the ANSWERED option, which
 *     excludes the configured default. Hiding on a default nobody chose would strand the engineer
 *     on a tab they never saw.
 *
 * Neither MMG-4405 rule applies while showAllTabs is on (the engineer asked to see everything) or in
 * historical mode: a read-only past inspection has nothing to declutter, and hiding the recorded
 * answer on a tab whose other controls MMG-4268 already hid would render the tab blank.
 *
 * On a first visit only a gate the engineer set on this draft counts: a fresh draft is pre-filled with
 * every gate's default, and the first visit must show every tab. On a later visit the tab hides off the
 * SELECTED option, so a gate the last visit never answered hides on its Not In Use default. The gate's
 * dropdown hides only with its tab, so an In Use tab keeps it and the engineer can always change it.
 *
 * isGateNotInUse is the exception: it is gate truth, deliberately ungated by showAllTabs,
 * isHistoricalMode and isFirstVisit. Callers deciding what the engineer can DO - not what they can
 * see - must use it, so that a menu toggle can never change whether Save & Log is allowed.
 */
export interface ItabGateVisibility {
    gate: ImeasurementPoint | undefined;
    selectedOptionID: string | null;
    hideNonGateControls: boolean;
    hideGateControl: boolean;
    isTabHidden: boolean;
    isTabHideable: boolean;
    isGateNotInUse: boolean;
}

export const resolveTabGateVisibility = (
    measurementPoints: ImeasurementPoint[],
    answers: ImeasurementPointAnswer[] | undefined,
    gateSelections: { [gateID: string]: string | null },
    showAllTabs: boolean,
    isHistoricalMode: boolean,
    isFirstVisit: boolean,
    pickedGateIDs: readonly string[]
): ItabGateVisibility => {
    const gate = findInUseGate(measurementPoints);
    if (!gate) {
        return {
            gate: undefined,
            selectedOptionID: null,
            hideNonGateControls: false,
            hideGateControl: false,
            isTabHidden: false,
            isTabHideable: false,
            isGateNotInUse: false
        };
    }

    /*
     * The answers array is walked exactly once here: the selected ID is derived from the answered ID
     * rather than resolved separately, so both MMG-4405 flags and the dropdown value stay in step.
     */
    const answeredOptionID = resolveGateAnsweredOptionID(gate, answers, gateSelections);
    const selectedOptionID = applyDefaultFallback(gate, answeredOptionID);
    const isGateNotInUse = isInUseGateExplicitlyNotInUse(gate, answeredOptionID);
    const isPickedOnDraft = gateSelections[gate.id] !== undefined || pickedGateIDs.includes(gate.id);
    const hideableOptionID = isFirstVisit ? (isPickedOnDraft ? answeredOptionID : undefined) : selectedOptionID;
    const isTabHideable = isInUseGateExplicitlyNotInUse(gate, hideableOptionID);
    const isTabHidden = !showAllTabs && !isHistoricalMode && isTabHideable;

    return {
        gate,
        selectedOptionID,
        hideNonGateControls: !isInUseGateActive(gate, selectedOptionID),
        hideGateControl: isTabHidden,
        isTabHidden,
        isTabHideable,
        isGateNotInUse
    };
};

const renderHiddenControl = () => null;

/*
 * Hide already-built form control configs for one measurement point. Used both for points sitting
 * under an inactive gate (MMG-4268) and for an answered gate's own dropdown (MMG-4405), so the name
 * says what it does rather than why the caller wants it.
 *
 * Visibility-only override: the control stays in the form with its value and meta intact (so saved
 * answers are unchanged, and its valueChanges subscription still fires), but it renders nothing and
 * its validators are stripped so an empty required field on a hidden control cannot block Save & Log.
 */
export const hideMeasurementPointControls = (controls: { [key: string]: any }): { [key: string]: any } => {
    const hiddenControls: { [key: string]: any } = {};
    Object.keys(controls).forEach(key => {
        const config = controls[key];
        if (!config) {
            return;
        }
        hiddenControls[key] = {
            ...config,
            render: renderHiddenControl,
            ...(config.options ? { options: { ...config.options, validators: [] } } : {})
        };
    });
    return hiddenControls;
};
